import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import ProtectedRoute from './ProtectedRoute'
import ProfessorView from '@/components/Layout/ProfessorView'
import StudentView from '@/components/Layout/StudentView'
import RoomDetails from '@/components/Room/RoomDetails'
import ForgotPasswordScreen from '@/pages/ForgotPassword'
import AdvisorSettingsScreen from '@/pages/AdvisorSettings'
import LoginScreen from '@/pages/Login'
import PlayerGameScreen from '@/pages/PlayerGameScreen'
import RegistrationScreen from '@/pages/Registration'
import ResetPasswordScreen from '@/pages/ResetPassword'
import CreateOrEnterRoom from '@/pages/Rooms'
import VerifyCodeScreen from '@/pages/Verify'
import EmailVerificationScreen from '@/pages/EmailVerification'
import OtpRequiredScreen from '@/pages/OtpRequired'
import QrVerificationScreen from '@/pages/QrVerification'
import AdjudicationScreen from '@/pages/Adjudication'
import NotFoundScreen from '@/pages/NotFound'
import StudentDashboardScreen from '@/pages/StudentDashboard'
import WaitingRoomScreen from '@/pages/WaitingRoom'
import { UserRole } from '@/constants/room'
import JoinRoomScreen from '@/pages/JoinRoom'
import GameroomScenarioSettings from '@/pages/GameroomScenario'
import RoomAdvisorSettingsScreen from '@/pages/RoomAdvisorSettings'
import VerifyEmailScreen from '@/pages/VerifyEmail'
import { Toaster } from '@/components/ui/sonner'
import ProfileScreen from '@/pages/ProfileScreen'
import TimerSettings from '@/pages/TimerSettings'
import CommunicationsSettings from '@/pages/CommunicationsSettings'
import ResourceLimitsSettings from '@/components/settings/ResourceLimits/ResourceLimitsSettings'
import PlayerResourceLimitsView from '@/components/settings/ResourceLimits/PlayerResourceLimitsView'
import ROUTES from '@/routes/routes'
import { PROFESSOR_CONFIGURE_SEGMENTS } from '@/routes/routes'
import { ProfessorConfigureSegment } from '@/routes/routes'
import QuickControls from '@/components/Room/QuickControls'
import Monitoring from '@/components/Room/Monitoring'
import GameAnalysis from '@/components/ChatAnalysis/GameAnalysis'
import GameSummary from '@/components/ChatAnalysis/GameSummary'
import GuestPage from '@/pages/Guest'
import SimulationPlayersView from '@/components/Room/SimulationPlayersView'
import ConfigurationLayout from '@/components/Room/ConfigurationLayout'
import GameSetupWizardScreen from '@/pages/GameSetupWizard'
import ThirdPartyApiAccessScreen from '@/pages/ThirdPartyApiAccess'
import AIPlayerSettingsScreen from '@/pages/AIPlayerSettings'
import MCPOAuthCallback from '@/pages/MCPOAuthCallback'
import CharterPrepPage from '@/features/charter/pages/CharterPrepPage'
import CharterGroupPage from '@/features/charter/pages/CharterGroupPage'
import CharterMonitoringPage from '@/features/charter/pages/CharterMonitoringPage'
import CharterClasses from '@/components/CharterClass/CharterClasses'
import CharterClassPage from '@/components/CharterClass/CharterClassPage'
import CharterClassLanding from '@/components/Student/CharterClassLanding'
import CharterPrepMonitorPage from '@/features/charter/pages/CharterPrepMonitorPage'
import CharterGroupMonitorPage from '@/features/charter/pages/CharterGroupMonitorPage'

function RedirectToConfigure({ segment }: { segment: ProfessorConfigureSegment }) {
  const { roomId } = useParams<{ roomId: string }>()
  const to = roomId
    ? ROUTES.PROFESSOR.ROOM_CONFIGURE_SEGMENT(roomId, segment)
    : ROUTES.PROFESSOR.ROOMS
  return <Navigate to={to} replace />
}

function ConfigureIndexRedirect() {
  return <RedirectToConfigure segment={PROFESSOR_CONFIGURE_SEGMENTS.SCENARIO} />
}

function CharterViewRedirect() {
  const { roomId } = useParams<{ roomId: string }>()
  // A Charter Class has no game screen to fall back to — send it to the class
  // landing, which re-resolves where the student belongs.
  const isCharterClass = useSelector(
    (state: RootState) => state.room.currentRoom?.is_charter_only === true
  )
  if (!roomId) return <Navigate to={ROUTES.STUDENT.DASHBOARD} replace />
  return (
    <Navigate
      to={
        isCharterClass
          ? ROUTES.STUDENT.CHARTER_CLASS(roomId)
          : ROUTES.STUDENT.GAME(roomId)
      }
      replace
    />
  )
}

const AppRoutes = () => {
  const { user } = useSelector((state: RootState) => state.user)
  return (
    <BrowserRouter>
      <Toaster position="bottom-right" expand={true} visibleToasts={4} closeButton />
      <Routes>
        {/* Public Routes */}
        <Route
          path={ROUTES.ROOT}
          element={<Navigate to={ROUTES.LOGIN} replace />}
        />
        <Route path={ROUTES.GUEST} element={<GuestPage />} />
        <Route path='/mcp-connected' element={<MCPOAuthCallback />} />
        <Route path={ROUTES.REGISTER} element={<RegistrationScreen />} />
        <Route path={ROUTES.CONFIRM_EMAIL} element={<VerifyEmailScreen />} />
        <Route path={ROUTES.LOGIN} element={<LoginScreen />} />
        <Route
          path={ROUTES.FORGOT_PASSWORD}
          element={<ForgotPasswordScreen />}
        />
        <Route
          path={ROUTES.VERIFICATION.EMAIL}
          element={<EmailVerificationScreen />}
        />
        <Route path={ROUTES.VERIFICATION.OTP} element={<OtpRequiredScreen />} />
        <Route
          path={ROUTES.VERIFICATION.QR}
          element={<QrVerificationScreen />}
        />
        <Route path={ROUTES.VERIFY_CODE} element={<VerifyCodeScreen />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordScreen />} />

        {user?.role && (
          <Route path={ROUTES.PROFILE} element={<ProfileScreen />} />
        )}

        {user?.role === UserRole.Student ? (
          <Route
            path={ROUTES.ROOT}
            element={
              <ProtectedRoute>
                <StudentView />
              </ProtectedRoute>
            }
          >
            <Route
              path={ROUTES.STUDENT.DASHBOARD}
              element={<StudentDashboardScreen />}
            />
            <Route
              path={ROUTES.STUDENT.JOIN_ROOM(':roomId')}
              element={<JoinRoomScreen />}
            />
            <Route
              path={ROUTES.STUDENT.WAITING_ROOM(':roomId')}
              element={<WaitingRoomScreen />}
            />
            <Route
              path={ROUTES.STUDENT.GAME(':roomId')}
              element={<PlayerGameScreen />}
            />
            <Route
              path={ROUTES.STUDENT.CHARTER_CLASS(':roomId')}
              element={<CharterClassLanding />}
            />
            <Route
              path={ROUTES.STUDENT.CHARTER_PREP(':roomId')}
              element={<CharterPrepPage />}
            />
            <Route
              path={ROUTES.STUDENT.CHARTER_GROUP(':roomId')}
              element={<CharterGroupPage />}
            />
            <Route
              path={ROUTES.STUDENT.CHARTER_VIEW(':roomId')}
              element={<CharterViewRedirect />}
            />
            <Route
              path={ROUTES.STUDENT.CHARTER_VERSIONS(':roomId')}
              element={<CharterViewRedirect />}
            />
          </Route>
        ) : (
          <Route
            path={ROUTES.ROOT}
            element={
              <ProtectedRoute>
                <ProfessorView />
              </ProtectedRoute>
            }
          >
            <Route
              path={ROUTES.PROFESSOR.ROOMS}
              element={<CreateOrEnterRoom />}
            />
            <Route
              path={ROUTES.PROFESSOR.CHARTER_CLASSES}
              element={<CharterClasses />}
            />
            <Route
              path={ROUTES.PROFESSOR.CHARTER_CLASS(':roomId')}
              element={<CharterClassPage />}
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_DETAILS(':roomId')}
              element={<RoomDetails />}
            />
            <Route
              path={ROUTES.PROFESSOR.GAME_SETUP_WIZARD(':roomId')}
              element={<GameSetupWizardScreen />}
            />
            <Route
              path={ROUTES.PROFESSOR.GAME(':roomId')}
              element={<PlayerGameScreen />}
            />

            <Route
              path={ROUTES.PROFESSOR.ROOM_ADJUDICATE(':roomId')}
              element={<AdjudicationScreen />}
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_SETTINGS(':roomId')}
              element={
                <RedirectToConfigure segment={PROFESSOR_CONFIGURE_SEGMENTS.ADVISORS} />
              }
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_CONFIGURATION(':roomId')}
              element={
                <RedirectToConfigure segment={PROFESSOR_CONFIGURE_SEGMENTS.SCENARIO} />
              }
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_ADVISOR_SETTINGS(':roomId')}
              element={<RoomAdvisorSettingsScreen />}
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_TIMER_SETTINGS(':roomId')}
              element={
                <RedirectToConfigure segment={PROFESSOR_CONFIGURE_SEGMENTS.TIMERS} />
              }
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_COMMUNICATIONS(':roomId')}
              element={
                <RedirectToConfigure
                  segment={PROFESSOR_CONFIGURE_SEGMENTS.COMMUNICATIONS}
                />
              }
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_RESOURCE_LIMITS(':roomId')}
              element={
                <RedirectToConfigure
                  segment={PROFESSOR_CONFIGURE_SEGMENTS.RESOURCE_LIMITS}
                />
              }
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_CONFIGURE(':roomId')}
              element={<ConfigurationLayout />}
            >
              <Route index element={<ConfigureIndexRedirect />} />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.SCENARIO}
                element={<GameroomScenarioSettings />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.TIMERS}
                element={<TimerSettings />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.COMMUNICATIONS}
                element={<CommunicationsSettings />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.RESOURCE_LIMITS}
                element={<ResourceLimitsSettings />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.SHARED_ADVISOR_SETTINGS}
                element={<RoomAdvisorSettingsScreen />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.ADVISORS}
                element={<AdvisorSettingsScreen />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.AI_PLAYERS}
                element={<AIPlayerSettingsScreen />}
              />
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.API_ACCESS}
                element={<ThirdPartyApiAccessScreen />}
              />
              {/* Charter config moved into the Rounds & Timers tab (Round 0).
                  Redirect the legacy route so old links keep working. */}
              <Route
                path={PROFESSOR_CONFIGURE_SEGMENTS.CHARTER}
                element={
                  <RedirectToConfigure
                    segment={PROFESSOR_CONFIGURE_SEGMENTS.TIMERS}
                  />
                }
              />
            </Route>
            <Route
              path={ROUTES.PROFESSOR.ROOM_PLAYER_RESOURCE_LIMITS(':roomId')}
              element={<PlayerResourceLimitsView />}
            />
            <Route
              path={ROUTES.PROFESSOR.QUICK_CONTROLS(':roomId')}
              element={<QuickControls />}
            />
            <Route
              path={ROUTES.PROFESSOR.MONITORING(':roomId')}
              element={<Monitoring />}
            />
            <Route
              path={ROUTES.PROFESSOR.GAME_ANALYSIS(':roomId')}
              element={<GameAnalysis />}
            />
            <Route
              path={ROUTES.PROFESSOR.GAME_SUMMARY(':roomId')}
              element={<GameSummary />}
            />
            <Route
              path={ROUTES.PROFESSOR.SIMULATION_PLAYERS(':roomId')}
              element={<SimulationPlayersView />}
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_CHARTER_MONITORING(':roomId')}
              element={<CharterMonitoringPage />}
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_CHARTER_PREP_MONITOR(':roomId', ':memberId')}
              element={<CharterPrepMonitorPage />}
            />
            <Route
              path={ROUTES.PROFESSOR.ROOM_CHARTER_GROUP_MONITOR(':roomId', ':teamId')}
              element={<CharterGroupMonitorPage />}
            />
            <Route
              path={ROUTES.PROFESSOR.SETTINGS}
              element={<AdvisorSettingsScreen />}
            />
            <Route
              path={ROUTES.PROFESSOR.JOIN_ROOM(':roomId')}
              element={<JoinRoomScreen />}
            />
          </Route>
        )}
        <Route path={ROUTES.NOT_FOUND} element={<NotFoundScreen />} />
      </Routes>
    </BrowserRouter>
  )
}

export default AppRoutes
